import type { TranslationShape } from '../types';

export const pt = {
  common: {
    loading: 'Carregando...',
    initializing: 'Inicializando...',
    continue: 'Continuar',
    back: 'Voltar',
    retry: 'Tentar novamente',
    tryAgain: 'Tentar novamente',
    goBack: 'Voltar',
    cancel: 'Cancelar',
    close: 'Fechar',
    closeModal: 'Fechar modal',
    closeWindow: 'Fechar janela',
    startOver: 'Recomeçar',
    switchToMobile: 'Mudar para celular',
    enableCamera: 'Ativar câmera',
    returnToSite: 'Voltar ao site',
    notice: 'Aviso',
    done: 'Concluído',
    saving: 'Salvando...',
    processing: 'Processando...',
    error: 'Erro',
    moreInfo: 'Mais informações',
    productFallback: 'verificação',
  },
  ageEstimateUnavailable: {
    title: 'Não conseguimos verificar sua idade agora',
    description:
      'Ocorreu um problema do nosso lado ao analisar sua selfie. Você pode tentar novamente ou se verificar com seu documento usando a selfie que acabou de tirar.',
    descriptionIdOnly:
      'Ocorreu um problema do nosso lado ao analisar sua selfie. Você pode continuar se verificando com seu documento usando a selfie que acabou de tirar.',
    useId: 'Verificar com documento',
  },
  pipelineRecovery: {
    title: 'A verificação foi interrompida temporariamente',
    description:
      'Não foi possível continuar esta etapa agora. Sua verificação continua ativa.',
    retrying: 'Estamos tentando novamente. Mantenha esta página aberta.',
    readyToRetry: 'Tente novamente quando estiver pronto.',
    retryingAction: 'Tentando novamente…',
  },
  lifecycleRecovery: {
    loadTitle: 'Não foi possível carregar o próximo passo', loadDescription: 'Verifique sua conexão e tente novamente.', retryTitle: 'Vamos tentar novamente',
    retryDescription: 'Algo interrompeu este passo. Tente novamente.', retrySelfieTitle: 'Vamos tirar sua selfie de novo', retrySelfieDescription: 'Olhe para a câmera em um lugar bem iluminado e tente novamente.', retryIdTitle: 'Vamos tirar outra foto do seu documento', retryIdDescription: 'Verifique se o documento inteiro está visível e legível.', restartStepTitle: 'Vamos tentar este passo novamente',
    restartStepDescription: 'Precisamos reiniciar este passo para continuar.', startAgain: 'Recomeçar', beginAgain: 'Começar novamente',
    cannotContinueTitle: 'Esta verificação não pode continuar', cannotContinueDescription: 'Inicie uma nova verificação.', waitingTitle: 'Estamos concluindo este passo…',
    waitingDescription: 'Mantenha esta página aberta. Continuaremos automaticamente.', cancelledTitle: 'Verificação cancelada', completeTitle: 'Verificação concluída',
    closeDescription: 'Pode fechar esta janela.', returnDescription: 'Pode voltar ao site.', continueTitle: 'Pronto para continuar', continueDescription: 'Continue para o próximo passo.',
    rateLimitedTitle: 'Precisamos de um momento', rateLimitedDescription: 'Não podemos verificar o próximo passo agora. Sua verificação continua ativa.', checkStatus: 'Verificar status',
    finalizingTitle: 'Finalizando sua verificação…',
    finalizingDescription: 'Mantenha esta página aberta. Normalmente leva alguns segundos.',
    unconfirmedTitle: 'Ainda estamos confirmando seu resultado',
    unconfirmedDescription: 'Está demorando mais do que o normal. Você não precisará repetir nenhuma etapa.',
    checkAgain: 'Verificar novamente',
  },
  footer: {
    terms: 'Termos',
    privacy: 'Privacidade',
    biometric: 'Biometria',
    languageLabel: 'Idioma',
    copyright: '© {{year}} {{companyName}}. Todos os direitos reservados.',
  },
  mainScreen: {
    title: 'Verifique-se de forma anônima',
    description:
      'Verificação de idade rápida e privada. Nunca coletamos ou compartilhamos seus dados.',
    iconAlt: 'Verificação segura',
    primaryAction: 'Verificar agora',
    secondaryAction: 'Entrar com Emblem',
    separatorText: 'Já verificado?',
    missingSession:
      'A configuração da sessão está ausente. Inicie a verificação desde o início.',
    directAccessBlocked:
      'O acesso direto não é permitido. Inicie a verificação desde o início.',
    emblemResumeNotice:
      "Emblem sign-in wasn't completed. You can continue with verification here.",
    emblemNoReusableNotice:
      "You don't have a reusable Emblem pass for this site yet. You can continue with verification here.",
  },
  emblemStepUp: {
    title: 'One more verification step',
    description:
      'Your Emblem passkey worked, but this site requires a higher level of verification.',
    primaryAction: 'Continue verification',
    iconAlt: 'Emblem verification step up',
    detailTitle: 'What happens next',
    detailPoint1: 'Complete a quick SafePassage verification for this site.',
    detailPoint2:
      'After you finish, your Emblem passkey will be upgraded for future L2 sites.',
    privacyHint: 'You are still verifying privately. Sites only receive the result.',
  },
  loading: {
    title: 'Configurando sistema de verificação seguro',
    description: 'Suas informações pessoais nunca são coletadas, armazenadas ou compartilhadas',
    status: {
      initializing: 'Inicializando...',
      settingUpApiKey: 'Configurando chave da API...',
      ready: 'Pronto para iniciar a verificação',
      errorRetrying: 'Erro ao inicializar — tentando novamente...',
    },
    errors: {
      noApiKey: 'Nenhuma chave da API configurada. Forneça uma chave da API.',
    },
  },
  cameraAccess: {
    title: 'Acesso à câmera necessário',
    description: 'O acesso à câmera é necessário para verificar sua idade e continuar',
    continueDesktop: 'Continuar no computador',
    separatorText: 'OU',
    noWebcam: 'Não tem webcam?',
    privacyNotice: 'Sua imagem é processada instantaneamente e excluída imediatamente',
    qr: {
      title: 'Continuar no celular',
      description: 'Escaneie este código para concluir a verificação no celular',
      imageAlt: 'Código QR para verificação no celular',
      instruction: 'Aponte a câmera do seu celular para este código',
      waiting: 'Aguardando conexão...',
      generating: 'Gerando código QR...',
      errorMissingSession: 'ID da sessão ausente. Volte e tente novamente.',
      errorUnavailable: 'Não foi possível gerar o código QR. Tente novamente.',
      errorFailed: 'Falha ao gerar o código QR. Tente novamente.',
    },
  },
  cameraSetup: {
    defaultMessage: 'Posicione seu rosto no círculo',
    warmup: {
      preparing: 'Preparando sua verificação...',
      preparingDelayed:
        'A conexão com o serviço de verificação está demorando mais do que o esperado. Mantenha esta página aberta.',
      requesting: 'Aguardando permissão da câmera...',
      initializing: 'Iniciando sua câmera...',
      positioning: 'Preparando a imagem da câmera...',
      delayed:
        'Isso está demorando mais do que o esperado. Mantenha esta página aberta enquanto a câmera inicia.',
    },
    analysis: {
      title: 'Processando sua foto',
      description: 'Isso geralmente leva alguns segundos.',
      privacy: 'As imagens são processadas com segurança e não são armazenadas.',
      failureMessage: 'Não conseguimos concluir a verificação. Tente novamente.',
    },
    errors: {
      stalledTitle: 'A câmera não iniciou',
      interruptedTitle: 'A câmera foi interrompida',
      missingSessionId: 'ID da sessão ausente no resultado da verificação.',
      faceTimeout:
        'Tempo esgotado na detecção do rosto. Certifique-se de que seu rosto esteja visível na câmera.',
      faceTimeoutDetail:
        'Nenhum rosto foi detectado no tempo necessário. Tente novamente e certifique-se de que seu rosto esteja claramente visível no círculo.',
    },
  },
  cameraFeedback: {
    initial_position: 'Centralize seu rosto no quadro',
    initial_lighting: 'Procure boa iluminação',
    initial_stability: 'Mantenha a câmera estável',
    wide_zone_position: 'Centralize seu rosto no quadro',
    wide_zone_center: 'Centralize seu rosto no quadro',
    wide_too_close: 'Afaste-se um pouco',
    wide_off_center: 'Centralize seu rosto no quadro',
    wide_movement: 'Fique parado',
    wide_gaze: 'Olhe para frente',
    wide_success: 'Ótimo posicionamento!',
    zone_transition: 'Perfeito! Agora aproxime-se um pouco',
    great_now_move_closer: 'Perfeito! Agora aproxime-se um pouco',
    close_zone_position: 'Perfeito! Fique assim',
    close_zone_gaze: 'Olhe para frente',
    close_too_far: 'Aproxime-se um pouco',
    close_too_close: 'Afaste-se um pouco',
    close_off_center: 'Centralize seu rosto no quadro',
    close_movement: 'Fique parado',
    close_gaze: 'Olhe para frente',
    close_success: 'Perfeito! Fique assim',
    processing: 'Pronto! Processando...',
    no_face_detected: 'Nenhum rosto detectado',
    move_closer: 'Aproxime-se um pouco',
    move_further: 'Afaste-se um pouco',
    center_face: 'Centralize seu rosto no quadro',
    look_at_camera: 'Olhe para a câmera',
    improve_lighting: 'Vamos tentar novamente',
    good_position: 'Está ótimo!',
    move_closer_to_camera: 'Aproxime-se um pouco',
    move_further_from_camera: 'Afaste-se um pouco',
    good_wide_position: 'Ótimo posicionamento!',
    good_close_position: 'Perfeito! Fique assim',
  },
  cameraProcessing: {
    verifying: 'Ótimo trabalho! Processando sua verificação',
    analyzing: 'Analisando...',
  },
  idRequest: {
    l2: {
      title: 'Verificação com documento necessária',
      description:
        'Apresente um documento de identidade emitido pelo governo para concluir a verificação',
    },
    l1: {
      failed: {
        title: 'Nosso escaneamento não conseguiu confirmar sua idade',
        description: 'Precisaremos de uma verificação rápida com documento para continuar',
      },
      unavailable: {
        title: 'Vamos concluir com uma verificação rápida do documento',
        description:
          'Desta vez não conseguimos verificar sua idade pela selfie, então usaremos seu documento.',
      },
      additional: {
        title: 'Verificação adicional necessária',
        description:
          'Para garantir conformidade, precisamos verificar sua idade com um documento',
      },
    },
    primaryAction: 'Continuar',
    privacyNotice: 'Não armazenamos nem compartilhamos suas informações pessoais',
    privacyTooltip:
      'Seu documento é usado apenas para verificar sua idade. Ele nunca é salvo, armazenado ou compartilhado com o site solicitante. O site solicitante recebe apenas uma confirmação simples de que você atende ao requisito de idade.',
    iconAlt: 'Documento de identidade',
  },
  idCapture: {
    errorTitle: 'Falha na captura da foto',
    initializingTitle: 'Inicializando câmera',
    initializingDescription: 'Preparando captura do documento...',
    title: 'Tire uma foto da frente do seu documento',
    recaptureTitle: 'Não foi possível aceitar essa foto do documento',
    instructions:
      'Certifique-se de que seu documento esteja completamente visível, com texto legível e todas as bordas no quadro',
    recaptureInstructions:
      'Certifique-se de que o documento esteja nítido e totalmente visível e tire a foto novamente.',
    capturing: 'Capturando...',
    recaptureAction: 'Tirar foto novamente',
    captureAction: 'Escanear documento',
    capturedTitle: 'Foto capturada!',
    capturedDescription: 'Preparando para revisão...',
    cameraErrorTitle: 'Erro na câmera',
    cameraErrorAlt: 'Erro na câmera',
    cameraRecoveryDescription:
      'Tente novamente, troque de câmera se disponível ou verifique se este navegador tem permissão para usar a câmera.',
    cameraPermissionHelp:
      'No iPhone ou iPad, confira o acesso à câmera do Safari em Ajustes. No Chrome ou Safari, use o ícone de câmera na barra de endereços para permitir o acesso.',
    switchCameraAction: 'Trocar câmera',
  },
  idReview: {
    title: 'Revise seu documento escaneado',
    description: 'Certifique-se de que todas as informações estejam claramente visíveis',
    imageAlt: 'Documento capturado',
    guidance: 'Seu documento deve estar visível e legível.',
    missingImage: 'Nenhuma imagem capturada. Volte e capture seu documento.',
    submitAction: 'Continuar',
    retakeAction: 'Tirar novamente',
  },
  idProcessing: {
    title: 'Verificando seu documento',
    description:
      'Esse processo geralmente leva cerca de 20 a 30 segundos. Em alguns casos, pode levar até 60 segundos.',
    keepOpen: 'Mantenha esta página aberta enquanto a verificação é concluída.',
    info: 'Seu documento está sendo processado com segurança. Nenhuma informação pessoal é armazenada.',
    status: {
      initial: 'Verificação em andamento.',
      stillWorking: 'Ainda estamos verificando seu documento...',
      almostDone: 'Quase pronto. Obrigado pela sua paciência.',
    },
    steps: {
      received: 'Documento recebido',
      analyzing: 'Analisando informações',
      verifying: 'Verificando autenticidade',
      confirming: 'Confirmando idade',
    },
    errors: {
      noDocumentData: 'Nenhum dado do documento encontrado. Capture seu documento novamente.',
      noSessionId: 'Nenhum ID de sessão disponível. Reinicie o processo de verificação.',
      selfieFetchFailed:
        'Falha ao recuperar a selfie do servidor. Reinicie o processo de verificação.',
      noApiKey:
        'Chave da API não disponível. Atualize a página e reinicie o processo de verificação.',
      generic: 'A verificação falhou.',
    },
  },
  desktopWaiting: {
    title: 'Dispositivo conectado',
    description: 'Conclua a verificação no seu dispositivo móvel',
    iconAlt: 'Dispositivo móvel',
    keepOpen: 'Mantenha esta janela aberta',
    redirectInfoLine1: 'Você será redirecionado automaticamente quando',
    redirectInfoLine2: 'a verificação for concluída.',
    expiredTitle: 'Sessão expirada',
    expiredDescription: 'Sua sessão de verificação expirou. Comece novamente.',
  },
  returnToDesktop: {
    title: 'Volte ao seu computador',
    description:
      'Verificação no celular concluída. Volte ao navegador do seu computador para continuar.',
    iconAlt: 'Computador',
  },
  recovery: {
    title: 'Continuar verificação?',
    description: 'Encontramos uma sessão de verificação em andamento',
    lastSeen: 'Última vez: {{screen}}',
    progressLine: '{{age}} • {{progress}}',
    progress: {
      start: 'Apenas começando',
      early: 'No início do processo',
      good: 'Bom progresso',
      halfway: 'Mais da metade concluída',
      almost: 'Quase terminando',
      nearlyComplete: 'Quase concluído',
    },
    time: {
      minuteAgo: 'há {{count}} minuto',
      minutesAgo: 'há {{count}} minutos',
      secondAgo: 'há {{count}} segundo',
      secondsAgo: 'há {{count}} segundos',
    },
    screens: {
      main: 'Boas-vindas',
      emblemStepUp: 'Emblem Step Up',
      cameraSetup: 'Configuração da câmera',
      verification: 'Verificação de idade',
      idRequest: 'Documento necessário',
      idCapture: 'Captura do documento',
      idProcessing: 'Processando documento',
      idReview: 'Revisão da captura',
      success: 'Verificação concluída',
      failure: 'Verificação falhou',
      error: 'Erro técnico',
      unknown: '{{screenName}}',
    },
    actions: {
      continue: 'Continuar de onde parei',
      startFresh: 'Recomeçar',
    },
    capabilities: {
      connection: '✓ A conexão pode ser restaurada',
      camera: '✓ Configurações da câmera salvas',
      manual: 'Algumas configurações podem precisar ser reconfiguradas',
    },
    info: {
      continuingLabel: 'Continuando:',
      continuingDescription: 'Retome a verificação de onde você parou.',
      startFreshLabel: 'Recomeçar:',
      startFreshDescription: 'Inicie uma nova sessão de verificação.',
    },
  },
  errorScreen: {
    helpTitle: 'O que você pode fazer:',
    invalidSession: {
      title: 'Sessão inválida',
      description:
        'A sessão de verificação é inválida ou expirou. Tente iniciar o processo de verificação novamente.',
      primaryAction: 'Contatar suporte',
      suggestion1: 'Volte ao site do comerciante',
      suggestion2: 'Inicie o processo de verificação novamente',
      suggestion3: 'Entre em contato com o suporte se o problema persistir',
    },
    expiredSession: {
      title: 'Sessão expirada',
      description:
        'Sua sessão de verificação expirou. Volte ao site ou aplicativo onde você começou.',
      primaryAction: 'Voltar ao site',
      suggestion1: 'Volte ao site ou aplicativo onde você começou',
      suggestion2: 'Inicie uma nova verificação lá',
      suggestion3: 'Entre em contato com o site se precisar de ajuda',
    },
    network: {
      title: 'Problema de conexão',
      description:
        'Não foi possível conectar aos serviços de verificação. Verifique sua conexão com a internet e tente novamente.',
      suggestion1: 'Verifique sua conexão com a internet',
      suggestion2: 'Desative a VPN se estiver usando uma',
      suggestion3: 'Tente recarregar a página',
      suggestion4: 'Entre em contato com o suporte se o problema persistir',
    },
    unsupported: {
      title: 'Navegador não suportado',
      description:
        'Seu navegador não suporta os recursos necessários para verificação de idade. Use um navegador moderno como Chrome, Firefox ou Safari.',
      primaryAction: 'Baixar Chrome',
      suggestion1: 'Atualize seu navegador atual',
      suggestion2: 'Tente usar Chrome, Firefox ou Safari',
      suggestion3: 'Ative o JavaScript se estiver desativado',
    },
    cameraDenied: {
      title: 'Acesso à câmera necessário',
      description:
        'A verificação de idade requer acesso à câmera. Permita o acesso à câmera e tente novamente.',
      suggestion1: 'Clique no ícone da câmera na barra de endereços',
      suggestion2: 'Selecione "Permitir" para acesso à câmera',
      suggestion3: 'Recarregue a página após conceder a permissão',
    },
    missingReturnUrl: {
      title: 'Erro de configuração',
      description:
        'A sessão de verificação está sem a configuração necessária. Entre em contato com o comerciante para corrigir a integração.',
      primaryAction: 'Contatar comerciante',
      suggestion1: 'Volte ao site do comerciante',
      suggestion2: 'Entre em contato com a equipe de suporte do comerciante',
      suggestion3: 'Informe que a integração do {{productName}} precisa ser atualizada',
      suggestion4:
        'O comerciante precisa fornecer uma URL de retorno válida na configuração do SDK',
    },
    directAccess: {
      title: 'Acesso direto não permitido',
      description:
        'A verificação de idade deve ser acessada através de um site comerciante usando o SDK do {{productName}}. Você não pode acessar este serviço diretamente.',
      primaryAction: 'Fechar página',
      iconAlt: 'Acesso bloqueado',
      suggestion1: 'Volte ao site do comerciante onde você começou',
      suggestion2: 'Conclua a verificação de idade no processo de compra ou cadastro',
      suggestion3: 'Não salve ou compartilhe esta URL de verificação',
      suggestion4:
        'Cada sessão de verificação é única e deve ser iniciada pelo comerciante',
    },
    subscriptionRequired: {
      title: 'Assinatura necessária',
      description:
        'Esta conta precisa de uma assinatura ativa para verificações em produção. Entre em contato com seu administrador para atualizar o plano.',
      primaryAction: 'Contatar administrador',
      portalAction: 'Abrir portal de cobrança',
    },
    planLimitReached: {
      title: 'Limite mensal atingido',
      description:
        'Esta conta atingiu seu limite mensal de verificações. Entre em contato com seu administrador para atualizar o plano ou aguarde o próximo período de cobrança.',
      primaryAction: 'Contatar administrador',
      portalAction: 'Abrir portal de cobrança',
    },
    sandboxLimitReached: {
      title: 'Limite do sandbox atingido',
      description:
        'Esta conta atingiu seu limite de verificações no sandbox. Uma assinatura paga é necessária para continuar com verificações em produção.',
      primaryAction: 'Contatar administrador',
      portalAction: 'Abrir portal de cobrança',
    },
  },
  failure: {
    genericError: 'A verificação falhou',
    iconAlt: 'Verificação malsucedida',
    document: {
      title: 'Verificação malsucedida',
      description: 'Não conseguimos concluir sua verificação. Vamos tentar novamente.',
      reason1: 'Problemas na qualidade da imagem do documento',
      reason2: 'Informações não claramente visíveis',
      reason3: 'Tipo de documento não suportado',
      reason4: 'Requisitos de verificação de idade não atendidos',
    },
    face: {
      title: 'Verificação malsucedida',
      description: 'Não conseguimos concluir sua verificação. Vamos tentar novamente.',
      reason1: 'A verificação de vivacidade não passou',
      reason2: 'Condições de iluminação inadequadas',
      reason3: 'Problemas no posicionamento da câmera',
      reason4: 'Movimento durante o escaneamento',
      reason5: 'Rosto não claramente visível',
    },
    crossDeviceRestart: {
      title: 'Volte ao seu computador',
      description:
        'Esta verificação precisa ser reiniciada no navegador de desktop que exibiu o QR code. Volte para lá, clique em Tentar novamente e depois escaneie o novo QR code com o seu telefone.',
    },
    errors: {
      restartFailed:
        'Falha ao reiniciar a verificação. Atualize a página e tente novamente.',
      switchFailed: 'Falha ao mudar para celular. Atualize a página e tente novamente.',
    },
  },
  success: {
    title: 'Verificação de idade bem-sucedida',
    emblemTitle: 'Conta Emblem criada!',
    emblemDescription:
      'Sua verificação de idade portátil está pronta. Você pode usar sua conta Emblem para verificar sua idade em qualquer site que aceite Emblem.',
    iconAlt: 'Sucesso',
    continueAction: 'Continuar para o site',
    separatorText: 'pular futuras verificações',
    createEmblemAction: 'Criar conta Emblem',
    emblemEnrollmentComplete: 'Conta Emblem criada',
    readyDescription: 'Agora você pode acessar seu conteúdo',
    redirectingIn: 'Redirecionando em {{seconds}} segundos...',
    returnUrlInvalid:
      'A URL de retorno não está configurada ou é inválida. Clique em Continuar para prosseguir.',
    errors: {
      missingReturnUrl: 'URL de retorno ausente — erro de configuração do SDK do comerciante',
      emblemSignupFailed: 'Falha ao criar conta Emblem',
    },
  },
  emblem: {
    info: {
      ariaLabel: 'Saiba mais sobre o Emblem',
      trigger: 'O que é o Emblem?',
      title: 'O que é o Emblem?',
      paragraph1:
        'O Emblem salva sua verificação de idade com segurança, permitindo que você prove que tem 18+ sem compartilhar dados pessoais.',
      paragraph2:
        'Após verificar sua idade com o {{productName}}, você pode criar uma conta Emblem para pular futuras verificações em sites participantes. Sua verificação permanece privada; os sites só sabem que você está verificado.',
      linkLabel: 'Site do Emblem',
    },
    signupInfo: {
      ariaLabel: 'Saiba mais sobre criar uma conta Emblem',
      trigger: 'Por que criar uma conta?',
      title: 'Criar conta Emblem',
      intro: 'Salve sua verificação para uso futuro! Com uma conta Emblem, você pode:',
      bullet1: 'Pular futuras verificações de idade em sites participantes',
      bullet2: 'Manter suas informações pessoais privadas',
      bullet3: 'Acessar conteúdo restrito por idade instantaneamente',
      outro:
        'Sua verificação é armazenada com segurança. Os sites só sabem que você está verificado.',
      linkLabel: 'Site do Emblem',
    },
  },
  mobileCompletion: {
    title: 'Verificação concluída',
    description:
      'A verificação no celular foi concluída com sucesso. Você pode fechar esta janela e voltar ao seu computador.',
    iconAlt: 'Computador',
    closeWindowFallback:
      'Feche esta aba/janela manualmente para voltar ao seu computador.',
  },
  cameraErrorBoundary: {
    iconAlt: 'Erro na câmera',
    trySteps: 'Tente estes passos:',
    skipCamera: 'Continuar sem câmera',
    permission: {
      title: 'Acesso à câmera necessário',
      description: 'Para verificar sua idade, precisamos de acesso à sua câmera.',
      step1: 'Clique no ícone da câmera na barra de endereços do navegador',
      step2: 'Selecione "Permitir" para acesso à câmera',
      step3: 'Clique em "Tentar novamente" abaixo',
    },
    notFound: {
      title: 'Nenhuma câmera detectada',
      description: 'Não encontramos uma câmera no seu dispositivo.',
      step1: 'Verifique se a câmera está conectada corretamente',
      step2: 'Tente usar outro navegador',
      step3: 'Use um dispositivo com câmera integrada',
    },
    inUse: {
      title: 'Câmera em uso',
      description: 'Sua câmera está sendo usada por outro aplicativo.',
      step1: 'Feche outros aplicativos que possam estar usando a câmera',
      step2: 'Verifique chamadas de vídeo ou software de gravação',
      step3: 'Reinicie o navegador e tente novamente',
    },
    unsupported: {
      title: 'Navegador não suportado',
      description: 'Seu navegador não suporta acesso à câmera.',
      step1: 'Atualize para a versão mais recente do seu navegador',
      step2: 'Tente usar Chrome, Firefox, Safari ou Edge',
      step3: 'Ative o WebRTC nas configurações do navegador',
    },
    default: {
      title: 'Erro na câmera',
      description: 'Encontramos um problema ao acessar sua câmera.',
      step1: 'Verifique a conexão da câmera',
      step2: 'Recarregue a página e tente novamente',
      step3: 'Tente usar outro navegador',
    },
    platform: {
      chrome: 'Chrome/Edge:',
      chromeInstruction: 'Clique no ícone da câmera na barra de endereços',
      firefox: 'Firefox:',
      firefoxInstruction: 'Clique no ícone de permissões ao lado da URL',
      safari: 'Safari:',
      safariInstruction: 'Vá em Safari -> Configurações -> Sites -> Câmera',
    },
  },
  verificationErrorBoundary: {
    iconAlt: 'Erro',
    retryAttempt: 'Tentativa {{attempt}} de {{max}}',
    errorDetails: 'Detalhes do erro',
    errorType: 'Tipo de erro:',
    errorTypeGeneral: 'geral',
    componentStack: 'Pilha de componentes:',
    cameraDenied: {
      title: 'Acesso à câmera negado',
      description:
        'Permita o acesso à câmera nas configurações do navegador para continuar com a verificação.',
    },
    noCamera: {
      title: 'Nenhuma câmera encontrada',
      description: 'Certifique-se de que uma câmera funcional esteja conectada ao seu dispositivo.',
    },
    cameraError: {
      title: 'Erro na câmera',
      description:
        'Encontramos um problema com sua câmera. Verifique a câmera e tente novamente.',
    },
    documentFailed: {
      title: 'Falha na captura do documento',
      description:
        'Tivemos dificuldade em capturar seu documento. Garanta boa iluminação e tente novamente.',
    },
    connectionLost: {
      title: 'Conexão perdida',
      description:
        'Perdemos a conexão com nossos servidores. Verifique sua conexão com a internet e tente novamente.',
    },
    multipleErrors: {
      title: 'Múltiplos erros ocorreram',
      description:
        'Estamos tendo problemas persistentes. Tente novamente mais tarde ou entre em contato com o suporte.',
    },
    default: {
      title: 'Algo deu errado',
      description:
        'Encontramos um erro inesperado. Tente novamente ou recomece se o problema persistir.',
    },
  },
  errorState: {
    title: 'Algo deu errado',
  },
  notifications: {
    errorTitle: 'Erro',
    successTitle: 'Sucesso',
    infoTitle: 'Informação',
    cameraAccessDenied:
      'O acesso à câmera foi negado. Conceda as permissões da câmera e tente novamente.',
    noCameraFound: 'Nenhuma câmera encontrada. Certifique-se de que seu dispositivo tenha uma câmera.',
    cameraInUse: 'A câmera já está em uso por outro aplicativo.',
    networkError: 'Erro de rede. Verifique sua conexão e tente novamente.',
    unexpectedError: 'Ocorreu um erro inesperado. Tente novamente.',
  },
  cameraErrors: {
    notAllowed:
      'Precisamos de acesso à câmera para continuar. Selecione "Tentar novamente" para solicitar o acesso. Se o navegador não perguntar de novo, permita o acesso à câmera para este site nas configurações do navegador e depois tente novamente ou recarregue a página.',
    notAllowedAction: 'Clique em Permitir quando solicitada a permissão da câmera',
    security:
      'O acesso à câmera está bloqueado pelas configurações de segurança do navegador ou do dispositivo. Permita o acesso para este site nessas configurações e tente novamente ou recarregue a página.',
    securityAction:
      'Permita o acesso à câmera nas configurações de segurança do navegador ou do dispositivo',
    notFound: 'Nenhuma câmera encontrada. Certifique-se de que uma câmera esteja conectada.',
    notFoundAction: 'Conecte um dispositivo de câmera',
    notReadable: 'A câmera já está em uso por outro aplicativo.',
    notReadableAction: 'Feche outros aplicativos que estejam usando a câmera',
    overconstrained: 'Configurações da câmera não suportadas. Tentando com configurações padrão...',
    overconstrainedAction: 'Tentaremos novamente com configurações básicas da câmera',
    timeout: 'Tempo esgotado ao inicializar a câmera. Tente novamente.',
    timeoutAction: 'Tente novamente',
    videoStalled:
      'Sua câmera foi ligada, mas nenhuma imagem chegou. Selecione "Tentar novamente" para reiniciar a câmera. Se isso continuar acontecendo, feche outros aplicativos que usam a câmera ou recarregue a página.',
    videoStalledAction: 'Tente novamente para reiniciar a câmera',
    interrupted:
      'Sua câmera parou inesperadamente, possivelmente por causa de uma chamada ou de outro aplicativo. Selecione "Tentar novamente" para reiniciar a câmera.',
    interruptedAction: 'Tente novamente para reiniciar a câmera',
    initializationFailed: 'Falha na inicialização da câmera. Recarregue a página e tente novamente.',
    streamUnavailable: 'Transmissão da câmera indisponível. Verifique a conexão da câmera.',
    generic: 'Falha no acesso à câmera. Verifique a câmera e tente novamente.',
    genericAction: 'Recarregue a página e tente novamente',
  },
  brandHeader: {
    tagline: 'Verificação de idade',
    merchantLabel: 'Verificando para',
    secureLabel: 'Seguro',
  },
  faceMatchRecovery: {
    selfieRetry: {
      title: 'Vamos tentar outra selfie',
      message:
        'Desta vez não conseguimos associar sua selfie à foto do seu documento. Fotos de documentos podem ter anos, e mudanças na aparência ou na iluminação podem dificultar o reconhecimento.',
      tipsHeading: 'Experimente estas dicas para a próxima selfie:',
      tips: {
        straightOn: 'Olhe diretamente para a câmera e mantenha o rosto inteiro no enquadramento.',
        lighting: 'Procure um lugar bem iluminado, com a luz à sua frente.',
        expression:
          'Tente fazer a mesma expressão da foto do seu documento. Se você não estiver sorrindo na foto, mantenha uma expressão relaxada e neutra.',
        eyes: 'Certifique-se de que seus olhos estejam bem visíveis, sem reflexos ou óculos escuros.',
      },
      primaryAction: 'Tirar uma nova selfie',
      captureHeading: 'Para sua nova selfie',
      captureTips: {
        straightOn: 'Olhe diretamente para a câmera',
        lighting: 'Luz à sua frente',
        expression: 'Mesma expressão da foto do documento',
        eyes: 'Olhos bem visíveis, sem óculos escuros',
      },
      comparing: 'Comparando sua nova selfie com seu documento…',
    },
    repeated: {
      title: 'Ainda com problemas? Vamos tentar de novo.',
      message:
        'Uma foto de documento antiga ou uma iluminação diferente podem dificultar. Olhe para a câmera em um lugar bem iluminado e tente fazer a mesma expressão da foto do seu documento.',
      primaryAction: 'Tirar outra selfie',
    },
    help: {
      heading: 'Precisa de ajuda?',
      differentIdText:
        'Se você tiver outro documento com uma foto mais recente sua, pode usá-lo.',
      differentIdAction: 'Usar outro documento',
    },
    differentId: {
      captureTitle: 'Tire uma foto do seu outro documento',
      captureInstructions:
        'Tire uma foto da frente do outro documento. Certifique-se de que esteja nítido e totalmente visível.',
    },
    retainedIdUnavailable:
      'Precisamos de uma nova foto do seu documento para concluir. Certifique-se de que esteja nítido e totalmente visível.',
  },
} as const satisfies TranslationShape;
